/**
 * Manual query detail tour — /operator/manual-query/:id (and admin/*).
 *
 * Manual queries don't send email from the app. The flow is:
 *   create query → collect rates (airline + transporter) → finalise table
 *   → copy customer-safe HTML → paste into Gmail / Outlook.
 *
 * Anchors (verified on ManualQueryDetail.jsx):
 *   mq-detail-header      Sticky header (back, ref, checklist, calculator)
 *   mq-detail-templates   Template generation card (populate + copy HTML)
 *   mq-detail-partners    Airline + transporter rate entry section
 *   mq-detail-final-quote Final quote cost table
 *
 * Media (optional): drop GIFs at
 * public/walkthrough/manual-query-detail/step-<N>.gif. See
 * public/walkthrough/README.md for the recording workflow.
 */

const M = (step, alt, caption) => ({
  src: `/walkthrough/manual-query-detail/step-${step}.gif`,
  alt,
  caption,
});

export const manualQueryDetailTour = {
  id: "manual-query-detail",
  label: "Manual Query — Rates to Final Table",
  steps: [
    {
      target: "mq-detail-header",
      title: "Welcome to your manual query",
      description:
        "Manual queries live outside email — you'll collect rates here, build the cost table, then copy it into Gmail or your email client yourself. Use the back arrow to return to your list anytime.",
      bullets: [
        "Checklist tracks your stage progress",
        "Freight Calculator is one click away",
        "The reference (e.g. BT47 3XY) is shown so you can quote it in emails",
      ],
      placement: "bottom",
      media: M(0, "Manual query header bar"),
    },
    {
      target: "mq-detail-templates",
      title: "Optional — generate a rate-request email",
      description:
        "Need to ask an airline or transporter for a rate quickly? Populate a template here and copy the HTML to paste into Gmail. Purely a helper — skip this if you already have rates.",
      hint: "Templates are shortcuts, not required. You can enter rates directly below.",
      placement: "bottom",
      media: M(1, "Template generation card"),
    },
    {
      target: "mq-detail-partners",
      title: "Add airline & transporter rates",
      description:
        "This is the core step. Pick a saved airline and transporter (or type a new name to add one on the fly) and enter the agreed rate. These feed the cost table below.",
      bullets: [
        "Airline rates on the left, transporter rates on the right",
        "Save only enables when Final Rate > 0 (so blank entries can't go through)",
        "Not in your list? Type a name and save — it's created instantly",
        "Each rate you save appears as a row in the final table below",
      ],
      interactive: true,
      placement: "top",
      media: M(2, "Rate entry — airline + transporter side-by-side"),
    },
    {
      target: "mq-detail-final-quote",
      title: "Build the final quote, then copy it",
      description:
        "As rates arrive, the final cost table fills with every combination. Adjust margins inline, pick the winning row, then finalise. After finalising, two new buttons appear: Send to Customer Conversation (the existing email flow) and Copy for email (the manual-query flow).",
      bullets: [
        "Adjust Margin / Customs / Extra inline per row",
        "Use 'Preview as customer' (top-right pill in the modal) to hide internal columns",
        "Finalise locks one option and stamps a VANT Ref",
        "'Copy for email' puts the customer-safe table on your clipboard with inline styles — paste straight into Gmail / Outlook and it renders cleanly",
        "No email is sent from this page — you paste it yourself",
      ],
      interactive: true,
      placement: "top",
      media: M(3, "Finalise → Copy for email → Paste in Gmail", "End-to-end manual-query workflow."),
    },
  ],
};
